import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { parentUrl } from '../../utils/constants';
import { dateFormatter } from '../../utils/timeFormat';
import HedisMetricsMemberInfo from '../../Models/HedisMetrics/HedisMetricsMemberInfo.model';

type Props = {
    memberList: HedisMetricsMemberInfo[];
    hedisType?: string;
    status?: string;
};

const HedisDataGrid = ({ memberList, hedisType, status }: Props) => {
    const columns: GridColDef[] = [
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'AdmissionDate',
            headerName: 'Discharge Date',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.AdmissionDate) ? dateFormatter(new Date(row.AdmissionDate)) : '-'}`,
        },
        {
            field: 'DateOfService',
            headerName: 'Date Of Service',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.DateOfService) ? dateFormatter(new Date(row.DateOfService)) : '-'}`,
        },
        {
            field: 'AssessmentDate',
            headerName: 'Documented Date',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.AssessmentDate) ? dateFormatter(new Date(row.AssessmentDate)) : '-'}`,
        }
    ];

    const depressionFollowUpColumnsForMet: GridColDef[] = [
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateOfService',
            headerName: 'Date Of Service',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.DateOfService) ? dateFormatter(new Date(row.DateOfService)) : '-'}`,
        },
        {
            field: 'AssessmentDate',
            headerName: 'Documented Date',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.AssessmentDate) ? dateFormatter(new Date(row.AssessmentDate)) : '-'}`,
        }
    ];

    const depressionFollowUpColumnsForNotMet: GridColDef[] = [
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateOfService',
            headerName: 'Date Of Service',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.DateOfService) ? dateFormatter(new Date(row.DateOfService)) : '-'}`,
        },
        {
            field: 'AssessedBy',
            headerName: 'Assessed By',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.AssessedBy) ? row.AssessedBy : '-'}`,
        },
        {
            field: 'PHQ9Score',
            headerName: 'PHQ-9 Score',
            width: 130,
            valueGetter: (value, row) => row.PHQ9Score ?? '-',
        },
        {
            field: 'AppointmentDate',
            headerName: 'Next BHS Appointment',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.AppointmentDate) ? dateFormatter(new Date(row.AppointmentDate)) : '-'}`,
        },
    ];

    const depressionScreeningColumnsForMet: GridColDef[] = [
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateOfService',
            headerName: 'Date Of Service',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.DateOfService) ? dateFormatter(new Date(row.DateOfService)) : '-'}`,
        },
        {
            field: 'AssessedBy',
            headerName: 'Assessed By',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.AssessedBy) ? row.AssessedBy : '-'}`,
        },
        {
            field: 'PHQ9Score',
            headerName: 'PHQ-9 Score',
            width: 130,
            valueGetter: (value, row) => row.PHQ9Score ?? '-',
        },
        {
            field: 'AppointmentDate',
            headerName: 'Next BHS Appointment',
            width: 130,
            valueGetter: (value, row) => `${Boolean(row.AppointmentDate) ? dateFormatter(new Date(row.AppointmentDate)) : '-'}`,
        },
    ];

    const depressionScreeningColumnsForNotMet: GridColDef[] = [
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateOfService',
            headerName: 'Date Of Service',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.DateOfService) ? dateFormatter(new Date(row.DateOfService)) : '-'}`,
        },
        {
            field: 'DateInfo',
            headerName: 'Recent Appointment',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.DateInfo) ? dateFormatter(new Date(row.DateInfo)) : '-'}`,
        },
        {
            field: 'AppointmentDate',
            headerName: 'Next BHS Appointment',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.AppointmentDate) ? dateFormatter(new Date(row.AppointmentDate)) : '-'}`,
        },
    ];

    const bhDiagnosticColumn: GridColDef[] = [
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
            renderCell: (params) => {
                const color = params.row.StatusIndicator; // e.g., "Green", "Yellow", "Red"
                return (
                    <span style={{ color: color }}>
                        {params.row.Name}
                    </span>
                );
            },
        },
        {
            field: 'DateOfService',
            headerName: 'Assessed Date',
            width: 180,
            renderCell: (params) => {
                const color = params.row.StatusIndicator; // e.g., "Green", "Yellow", "Red"
                const dateValue = params.row.DateOfService
                    ? dateFormatter(new Date(params.row.DateOfService))
                    : '-';

                return (
                    <span style={{ color: color }}>
                        {dateValue}
                    </span>
                );
            },
        },
        {
            field: 'AppointmentDate',
            headerName: 'Upcoming BHS Appointment Date',
            width: 220,
            renderCell: (params) => {
                const color = params.row.StatusIndicator; // e.g., "Green", "Yellow", "Red"
                const dateValue = params.row.AppointmentDate
                    ? dateFormatter(new Date(params.row.AppointmentDate))
                    : '-';

                return (
                    <span style={{ color: color }}>
                        {dateValue}
                    </span>
                );
            },
        }
    ];

    const prepareColumns = () => {
        let columnsData = columns;
        if (hedisType === 'DepressionScreening') {
            columnsData = status === 'Met' ? depressionScreeningColumnsForMet : depressionScreeningColumnsForNotMet;
        } else if (hedisType === 'DepressionFollowUp') {
            columnsData = status === 'Met' ? depressionFollowUpColumnsForMet : depressionFollowUpColumnsForNotMet;
        } else if (hedisType === 'MedicationReconciliation') {
            columnsData = columnsData.filter((c) => c.headerName !== 'Documented Date' && c.headerName !== 'Assessed Date');
        } else if (hedisType === 'BHDiagnostic') {
            columnsData = bhDiagnosticColumn;
        } else {
            columnsData = columnsData.filter(
                (c) => c.headerName !== 'Documented Date' && c.headerName !== 'Discharge Date' && c.headerName !== 'Assessed Date'
            );
        }
        return columnsData;
    };

    const gridRows = useMemo(() => {
        return memberList.map((row, index) => ({
            ...row,
            GridRowId: `${row.ItemId ?? row.Id ?? 'row'}-${index}`,
        }));
    }, [memberList]);

    return (
        <DataGrid
            columns={prepareColumns()}
            rows={gridRows}
            getRowId={(row) => row.GridRowId}
            paginationMode="server"
            hideFooterPagination={true}
            hideFooter={true}
            onCellClick={(params: any) => {
                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
            }}
        />
    );
};

export default HedisDataGrid;
